import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useParams } from "react-router-dom";
import TherapistGallery from "@/components/TherapistGallery";
import ServiceRates from "@/components/ServiceRates";
import CustomerReviews from "@/components/CustomerReviews";
import PromoBanner from "@/components/PromoBanner";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LanguageProvider, useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import LoginPage from "@/pages/LoginPage";
import AdminDashboard from "@/pages/AdminDashboard";
import TherapistPortal from "@/pages/TherapistPortal";
import { Button } from "@/components/ui/button";
import BookingForm from "@/components/BookingForm";
import CustomerChat from "@/components/CustomerChat";
import AdminChat from "@/components/AdminChat";
import ReviewPage from "@/components/ReviewPage";
import PwaInstallPrompt from "@/components/PwaInstallPrompt";
import FreelancePage from "@/pages/FreelancePage";
import ProfilesPage from "@/pages/ProfilesPage";
import { LogIn, MessageCircle, CalendarDays, ArrowLeft, Sparkles } from "lucide-react";

const ReviewRoute = () => { const { bookingId } = useParams(); return <ReviewPage bookingId={bookingId || ''} />; };

const BookingPage = () => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-white">
      <header className="pt-6 pb-2">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex justify-between items-center mb-4">
            <a href="/" data-testid="back-to-home-link">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                {t('common.back') || 'Back'}
              </Button>
            </a>
            <LanguageSwitcher variant="outline" />
          </div>
          <div className="flex flex-col items-center justify-center gap-2 mb-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_spa-bookings-5/artifacts/syk2guux_Angel-Massage-logo-png.png" 
              alt="Angel Massage Logo"
              className="w-full max-w-xs h-auto object-contain"
            />
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 pb-12">
        <BookingForm />
        <CustomerReviews />
      </main>
    </div>
  );
};

// Track page views on route changes for Google Analytics
const GaPageTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', { page_path: location.pathname + location.search });
    }
  }, [location]);
  return null;
};

// Auto-redirect book.angel-massage.shop to /book
const BookSubdomainRedirect = () => {
  const location = useLocation();
  useEffect(() => {
    if (window.location.hostname.startsWith('book.') && location.pathname === '/') {
      window.location.replace('/book');
    }
  }, [location]);
  return null;
};

function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <GaPageTracker />
            <BookSubdomainRedirect />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/book" element={<BookingPage />} />
              <Route path="/chat/:bookingId" element={<CustomerChat />} />
              <Route path="/admin-chat/:bookingId" element={<AdminChat />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/admin" element={<AdminDashboard />} />
              <Route path="/therapist" element={<TherapistPortal />} />
              <Route path="/review/:bookingId" element={<ReviewRoute />} />
              <Route path="/freelance" element={<FreelancePage />} />
              <Route path="/profiles" element={<ProfilesPage />} />
            </Routes>
            <PwaInstallPrompt />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const { t } = useLanguage();
  const [allUnavailable, setAllUnavailable] = useState(false);

  useEffect(() => {
    const checkAvailability = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/therapists`);
        if (res.ok) {
          const therapists = await res.json();
          const active = therapists.filter((t: any) => t.is_active);
          setAllUnavailable(active.length > 0 && active.every((t: any) => t.availability_status !== 'available'));
        }
      } catch {}
    };
    checkAvailability();
    const interval = setInterval(checkAvailability, 15000);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="min-h-screen">
      {/* Maintenance Banner — shown when ALL therapists are unavailable */}
      {allUnavailable && (
        <div className="bg-red-600 text-white py-3 px-4 overflow-hidden" data-testid="maintenance-banner">
          <style>{`
            @keyframes flashMaintenance {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.4; }
            }
            .maintenance-flash {
              animation: flashMaintenance 1.2s ease-in-out infinite;
            }
          `}</style>
          <div className="max-w-6xl mx-auto text-center maintenance-flash">
            <p className="text-sm sm:text-base font-bold">
              We are currently improving some features on our website and will be back online shortly
            </p>
            <p className="text-sm sm:text-base font-bold mt-1">
              ขณะนี้เรากำลังปรับปรุงฟีเจอร์บางอย่างบนเว็บไซต์ และจะกลับมาให้บริการในเร็วๆ นี้
            </p>
          </div>
        </div>
      )}

      {/* Promo Banner — admin-editable, auto-expires */}
      <PromoBanner />

      {/* LINE Discount Banner */}
      <div className="bg-[#00B900] text-white py-3 px-4 overflow-hidden" data-testid="line-promo-banner">
        <div className="max-w-6xl mx-auto flex items-center justify-center gap-3 text-center">
          <style>{`
            @keyframes scrollText {
              0% { transform: translateX(100%); }
              100% { transform: translateX(-100%); }
            }
            @keyframes flash {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.3; }
            }
            .scroll-text {
              display: inline-flex;
              align-items: center;
              gap: 12px;
              animation: scrollText 12s linear infinite;
              white-space: nowrap;
            }
            .flash-discount {
              animation: flash 1s ease-in-out infinite;
            }
          `}</style>
          <div className="scroll-text">
            <MessageCircle className="w-5 h-5 shrink-0" />
            <p className="text-sm sm:text-base font-semibold tracking-wide">
              Book via LINE and receive <span className="text-yellow-200 text-base sm:text-lg font-bold flash-discount">10% OFF</span> all services! Any questions? Message us on LINE and ask!
              <a href="https://line.me/R/ti/p/@angelmassage" target="_blank" rel="noopener noreferrer" className="underline ml-2 hover:text-yellow-200 transition-colors">Add us @angelmassage</a>
            </p>
            <MessageCircle className="w-5 h-5 shrink-0" />
            <p className="text-sm sm:text-base font-semibold tracking-wide">
              Book via LINE and receive <span className="text-yellow-200 text-base sm:text-lg font-bold flash-discount">10% OFF</span> all services! Any questions? Message us on LINE and ask!
              <a href="https://line.me/R/ti/p/@angelmassage" target="_blank" rel="noopener noreferrer" className="underline ml-2 hover:text-yellow-200 transition-colors">Add us @angelmassage</a>
            </p>
          </div>
        </div>
      </div>

      <header className="pt-8 pb-6">
        <div className="max-w-6xl mx-auto px-4">
          {/* Language Switcher */}
          <div className="flex justify-end mb-4">
            <LanguageSwitcher variant="outline" />
          </div>
          
          <div className="flex flex-col items-center justify-center gap-4 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_spa-bookings-5/artifacts/syk2guux_Angel-Massage-logo-png.png" 
              alt="Angel Massage - Outcall Massage Service Bangkok"
              className="w-full max-w-xl h-auto object-contain"
            />
          </div>
          <h1 className="text-center text-xl sm:text-2xl lg:text-3xl font-semibold text-gray-800 mt-4 max-w-3xl mx-auto leading-relaxed px-2">
            {t('home.heroTitle')}
          </h1>
          <p className="text-center text-base sm:text-lg text-gray-600 mt-3 max-w-2xl mx-auto leading-relaxed px-2">
            {t('home.heroSubtitle')}
          </p>
          
          {/* Action Buttons */}
          <div className="flex justify-center gap-4 mt-6 flex-wrap">
            <a 
              href="https://line.me/R/ti/p/@angelmassage" 
              target="_blank" 
              rel="noopener noreferrer"
              data-testid="add-friend-line-btn"
            >
              <Button 
                size="lg" 
                className="gap-2 bg-[#00B900] hover:bg-[#00A000] text-white font-semibold px-8"
              >
                <MessageCircle className="w-5 h-5" />
                {t('home.addFriendOnLine')}
              </Button>
            </a>
            <a 
              href="/book" 
              data-testid="book-now-btn"
            >
              <Button 
                size="lg" 
                className="gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-8"
              >
                <CalendarDays className="w-5 h-5" />
                {t('home.bookNow')}
              </Button>
            </a>
          </div>
          <p className="text-center text-sm sm:text-base font-bold italic text-[#00B900] mt-3" data-testid="line-question-note">
            {t('home.lineQuestionNote')}
          </p>

          {/* Angel Freelance highlighted button */}
          <div className="flex justify-center mt-8">
            <a href="/profiles" data-testid="angel-freelance-btn">
              <Button
                size="lg"
                className="gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold px-10 py-6 text-base sm:text-lg rounded-full shadow-lg shadow-purple-300/60 transition-all hover:scale-105"
              >
                <Sparkles className="w-5 h-5" />
                {t('home.freelanceButton')}
              </Button>
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 pb-12">
        <TherapistGallery />
        <ServiceRates />
        <CustomerReviews />
        
        {/* Book Now Call-to-Action Section */}
        <section className="mt-16 mb-8" data-testid="booking-cta">
          <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-8 text-center shadow-lg border border-pink-100">
            <h2 className="text-3xl font-bold mb-4">{t('home.bookNow')}</h2>
            <p className="text-gray-600 mb-6 max-w-xl mx-auto">
              {t('booking.formDescription')}
            </p>
            <div className="flex justify-center gap-4 flex-wrap">
              <a 
                href="/book" 
                data-testid="booking-cta-btn"
              >
                <Button 
                  size="lg" 
                  className="gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-10 py-6 text-lg"
                >
                  <CalendarDays className="w-6 h-6" />
                  {t('home.bookNow')}
                </Button>
              </a>
              <a 
                href="https://line.me/R/ti/p/@angelmassage" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button 
                  size="lg" 
                  variant="outline"
                  className="gap-2 border-[#00B900] text-[#00B900] hover:bg-[#00B900] hover:text-white font-semibold px-10 py-6 text-lg"
                >
                  <MessageCircle className="w-6 h-6" />
                  {t('home.addFriendOnLine')}
                </Button>
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="py-8 mt-12 border-t">
        <div className="max-w-6xl mx-auto px-4 text-center opacity-70">
          <p>{t('home.footer')}</p>
          <div className="mt-4">
            <Link to="/login">
              <Button variant="ghost" size="sm" className="gap-2 text-gray-400 hover:text-gray-600">
                <LogIn className="w-3 h-3" />
                {t('home.staffPortal')}
              </Button>
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;